import React from 'react';
import { IconButton } from '@noths/polaris-design-system-core-components';

import { ELLIPSIS, getPaginationItems } from './getPaginationItems';
import * as styles from './Pagination.styles';

export interface PaginationProps {
  ariaLabel: string;
  currentPage: number;
  getPageHref: (page: number) => string;
  totalPages: number;
}

const NARROW_MAX_NUMBERS = 3;
const WIDE_MAX_NUMBERS = 9;

const PageList = ({
  currentPage,
  getPageHref,
  listStyles,
  maxNumbers,
  totalPages,
}: Omit<PaginationProps, 'ariaLabel'> & {
  listStyles: typeof styles.narrowPages;
  maxNumbers: number;
}) => {
  const items = getPaginationItems(currentPage, totalPages, maxNumbers);

  return (
    <ul css={listStyles}>
      {items.map((item, index) =>
        item === ELLIPSIS ? (
          // At most one ellipsis each side of the current page, so the side is a stable key.
          <li aria-hidden css={styles.ellipsis} key={index < items.length / 2 ? 'start' : 'end'}>
            ···
          </li>
        ) : (
          <li key={item}>
            <a
              aria-current={item === currentPage ? 'page' : undefined}
              aria-label={`Page ${item}`}
              css={styles.page}
              href={getPageHref(item)}
            >
              {item}
            </a>
          </li>
        ),
      )}
    </ul>
  );
};

/** Plain links to each page, so every page is reachable without JavaScript and by crawlers. */
export const Pagination = ({
  ariaLabel,
  currentPage,
  getPageHref,
  totalPages,
}: PaginationProps) => {
  if (totalPages <= 1) {
    return null;
  }

  const listProps = { currentPage, getPageHref, totalPages };

  return (
    <nav aria-label={ariaLabel} css={styles.nav}>
      <div css={styles.row}>
        {currentPage > 1 ? (
          <IconButton
            aria-label="Previous page"
            href={getPageHref(currentPage - 1)}
            icon="actionBack"
            rel="prev"
          />
        ) : (
          <IconButton aria-label="Previous page" disabled icon="actionBack" />
        )}
        <PageList {...listProps} listStyles={styles.narrowPages} maxNumbers={NARROW_MAX_NUMBERS} />
        <PageList {...listProps} listStyles={styles.widePages} maxNumbers={WIDE_MAX_NUMBERS} />
        {currentPage < totalPages ? (
          <IconButton
            aria-label="Next page"
            href={getPageHref(currentPage + 1)}
            icon="actionForward"
            rel="next"
          />
        ) : (
          <IconButton aria-label="Next page" disabled icon="actionForward" />
        )}
      </div>
    </nav>
  );
};
